import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import KabXLogo from "../assets/icons/KABX_logo.svg"
import "./Header.css";

const HEADER_OFFSET = 80;

const CloseIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path fill="currentColor" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 1 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.9a1 1 0 0 0 1.41-1.41L13.41 12l4.9-4.89a1 1 0 0 0 0-1.4z" />
  </svg>
);

const MenuIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path fill="currentColor" d="M4 6h16v2H4zM4 11h16v2H4zM4 16h16v2H4z" />
  </svg>
);

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const lastHashRef = useRef(window.location.hash);
  const [open, setOpen] = useState(false);
  const offcanvasRef = useRef(null);

  // header shadow on scroll (cheap)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // update active section & URL hash while scrolling
  useEffect(() => {
    const sections = Array.from(document.querySelectorAll("section[id]"));
    if (!sections.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (!visible.length) return;
        visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const id = visible[0].target.id;
        if (!id) return;
        const newHash = `#${id}`;
        if (lastHashRef.current !== newHash) {
          lastHashRef.current = newHash;
          setActive(id);
          window.history.replaceState(null, "", newHash);
        }
      },
      { root: null, rootMargin: "-20% 0px -50% 0px", threshold: [0.25, 0.5, 0.75] }
    );

    sections.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  // Lock body scroll when offcanvas open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Handle scrolling to hash on load or cross-page navigation
  useEffect(() => {
    if (location.pathname === "/" && window.location.hash) {
      const id = window.location.hash.substring(1);
      let attempts = 0;
      
      const checkAndScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
          window.scrollTo({ top, behavior: "smooth" });
          setActive(id);
        } else if (attempts < 15) {
          attempts++;
          setTimeout(checkAndScroll, 50);
        }
      };

      const timer = setTimeout(checkAndScroll, 100);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.hash]);

  // close when clicking outside the panel (overlay)
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) setOpen(false);
  };

  // Smooth scroll on clicking a nav item
  const scrollToSection = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      setOpen(false);
      return;
    }

    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });

    setActive(id);
    lastHashRef.current = `#${id}`;
    try { window.history.pushState(null, "", `#${id}`); } catch (_) { }
    setOpen(false); // close mobile menu when clicking
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
      setOpen(false);
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    setActive("home");
    lastHashRef.current = "";
    try { window.history.pushState(null, "", "/"); } catch (_) { }
    setOpen(false);
  };

  // nav items - keep in sync with your sections
  const NAV = [
    { id: "home", label: "Home" },
    { id: "about", label: "About" },
    { id: "why-KabX", label: "Why KabX?" },
    { id: "safety", label: "Safety" },
    { id: "ride", label: "Ride" },
    { id: "drive", label: "Drive" },
    { id: "impact", label: "Impact" },
    { id: "contact", label: "Contact" },
    { id: "download-app", label: "Download App", isButton: true },
  ];

  return (
    <>
      <header className={`header ${scrolled ? "scrolled" : ""}`}>
        <div className="header-container">
          <div className="left">
            <div className="logo" onClick={handleLogoClick} role="button" tabIndex={0} aria-label="Go to home">
              <img
                src={KabXLogo}
                alt="KabX"
                className="logo-img"
              />
            </div>
          </div>

          <nav className="nav-menu" aria-label="Main navigation">
            {NAV.slice(0, NAV.length - 1).map((n) => (
              <button
                key={n.id}
                onClick={() => scrollToSection(n.id)}
                className={`nav-link ${active === n.id ? "active" : ""}`}
              >
                {n.label}
              </button>
            ))}
            {/* Download as button */}
            <Button
              className={`download-btn ${active === "download-app" ? "active" : ""}`}
              onClick={() => scrollToSection("download-app")}
            >
              Download App
            </Button>
          </nav>

          {/* mobile menu toggle */}
          <button
            className="mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </header>

      {/* Offcanvas + overlay */}
      <div
        className={`offcanvas-overlay ${open ? "open" : ""}`}
        onClick={handleOverlayClick}
        aria-hidden={!open}
      >
        <aside
          className={`offcanvas-panel ${open ? "open" : ""}`}
          ref={offcanvasRef}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile menu"
        >
          <div className="offcanvas-header">
            <div className="offcanvas-logo" onClick={handleLogoClick} role="button" tabIndex={0}>
              <img
                src={KabXLogo}
                alt="KabX"
                className="logo-img"
              />
            </div>
            <button className="offcanvas-close" onClick={() => setOpen(false)} aria-label="Close menu">
              <CloseIcon />
            </button>
          </div>

          <div className="offcanvas-body">
            {NAV.map((n) =>
              n.isButton ? (
                <div key={n.id} className="offcanvas-item">
                  <Button className={`download-btn full ${active === n.id ? "active" : ""}`} onClick={() => scrollToSection(n.id)}>
                    {n.label}
                  </Button>
                </div>
              ) : (
                <button
                  key={n.id}
                  onClick={() => scrollToSection(n.id)}
                  className={`offcanvas-link ${active === n.id ? "active" : ""}`}
                >
                  {n.label}
                </button>
              )
            )}
          </div>
        </aside>
      </div>
    </>
  );
};

export default Header;
